import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getAllCollections } from "../../redux/apiCall/collectionCall";
import axios from "axios";
import AdminFormLayout from "../components/AdminFormLayout";
import ButtonComponent from "../../components/ButtonComponent";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { apiUrl } from "../../config/api";
import {
  appendImages,
  appendKeepImages,
  ensureAdminAuth,
  getApiErrorMessage,
  normalizeCategoryId,
} from "../../utils/adminApi";

const NotSaleEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const collections = useSelector((state) => state.collection.collections);

  const productId = location.state?.productId;

  const [loading, setLoading] = useState(true);
  const [existingImages, setExistingImages] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    collectionId: "",
    images: [],
  });

  useEffect(() => {
    dispatch(getAllCollections());
  }, [dispatch]);

  useEffect(() => {
    if (!productId) {
      toast.error("Product not found.");
      navigate("/admin/notsale");
      return;
    }

    const loadProduct = async () => {
      try {
        const { data } = await axios.get(apiUrl(`/api/products/${productId}`));
        const product = data.data;

        setFormData({
          name: product.name || "",
          description: product.description || "",
          collectionId: normalizeCategoryId(product.collectionId),
          images: [],
        });
        setExistingImages(
          Array.isArray(product.images) && product.images.length > 0
            ? product.images
            : product.image
            ? [product.image]
            : []
        );
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Failed to load product"));
        navigate("/admin/notsale");
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productId, navigate]);

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleImageChange = (e) => {
    const newFiles = Array.from(e.target.files);
    if (existingImages.length + newFiles.length > 5) {
      toast.error("Maximum 5 images total (current + new).");
      e.target.value = "";
      return;
    }
    setFormData((prev) => ({ ...prev, images: newFiles }));
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveNewImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      const headers = ensureAdminAuth();
      const collectionId = normalizeCategoryId(formData.collectionId);

      if (!collectionId) {
        toast.error("Please select a collection.");
        return;
      }

      const descriptionText = (formData.description || "").replace(/<[^>]*>/g, "").trim();
      if (!descriptionText) {
        toast.error("Product description is required.");
        return;
      }

      if (existingImages.length + formData.images.length === 0) {
        toast.error("Product must have at least one image.");
        return;
      }

      const productData = new FormData();
      productData.append("name", formData.name);
      productData.append("description", formData.description);
      productData.append("collectionId", collectionId);
      appendKeepImages(productData, existingImages);
      if (formData.images.length > 0) {
        appendImages(productData, formData.images);
      }

      await axios.put(apiUrl(`/api/products/${productId}`), productData, {
        headers,
        timeout: 120000,
      });

      toast.success("Product updated successfully");
      navigate("/admin/notsale");
    } catch (error) {
      console.error("Update failed:", error?.response?.data || error);
      toast.error(getApiErrorMessage(error, "Failed to update product"));
    }
  };

  if (loading) {
    return (
      <AdminFormLayout title="Edit Product" backTo="/admin/notsale" backLabel="Back to Not For Sale">
        <p>Loading product...</p>
      </AdminFormLayout>
    );
  }

  return (
    <AdminFormLayout title="Edit Product" backTo="/admin/notsale" backLabel="Back to Not For Sale">
      <form encType="multipart/form-data" onSubmit={handleUpdate}>
        <div className="admin-form-group">
          <label htmlFor="name">Product Name</label>
          <input
            type="text"
            className="form-control admin-all-forms-inputs"
            id="name"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="admin-form-group">
          <label htmlFor="description">Product Description</label>
          <ReactQuill
            id="description"
            theme="snow"
            value={formData.description}
            onChange={handleDescriptionChange}
            className="admin-all-forms-inputs-quill"
          />
        </div>

        {existingImages.length > 0 && (
          <div className="admin-form-group">
            <label>Current Images</label>
            <div className="admin-image-preview-grid">
              {existingImages.map((img, idx) => (
                <div key={`${img}-${idx}`} className="admin-image-preview">
                  <img
                    src={img}
                    alt={`Product ${idx + 1}`}
                    className="admin-table-image"
                    width={80}
                    height={80}
                  />
                  <button
                    type="button"
                    className="admin-image-remove"
                    onClick={() => handleRemoveExistingImage(idx)}
                    aria-label={`Remove image ${idx + 1}`}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
            <small className="text-muted">
              Click the red X on an image to remove it. Add new ones below (max 5 total).
            </small>
          </div>
        )}

        {formData.images.length > 0 && (
          <div className="admin-form-group">
            <label>New Images to Upload</label>
            <div className="admin-image-preview-grid">
              {formData.images.map((file, idx) => (
                <div key={`${file.name}-${idx}`} className="admin-image-preview">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="admin-table-image"
                    width={80}
                    height={80}
                  />
                  <button
                    type="button"
                    className="admin-image-remove"
                    onClick={() => handleRemoveNewImage(idx)}
                    aria-label={`Remove new image ${idx + 1}`}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="admin-form-group">
          <label htmlFor="images">
            Add Product Images ({existingImages.length + formData.images.length}/5)
          </label>
          <input
            type="file"
            className="form-control admin-all-forms-inputs"
            id="images"
            name="images"
            accept="image/*"
            onChange={handleImageChange}
            multiple
          />
        </div>

        <div className="admin-form-group">
          <label htmlFor="collectionId">Product Collection</label>
          <select
            className="form-select admin-all-forms-inputs"
            id="collectionId"
            name="collectionId"
            value={normalizeCategoryId(formData.collectionId)}
            onChange={(e) =>
              setFormData({ ...formData, collectionId: e.target.value })
            }
            required
          >
            {collections.map((collection) => (
              <option key={collection._id} value={collection._id}>
                {collection.collectionName}
              </option>
            ))}
          </select>
        </div>

        <ButtonComponent type="submit">Update Product</ButtonComponent>
      </form>
    </AdminFormLayout>
  );
};

export default NotSaleEdit;
